// API Types for Sphero BPS Projects API

import {
  cloneProjectVersion,
  CURRENT_FRAMEWORK_VERSION,
  CURRENT_PROJECT_VERSION,
  type ProjectVersion,
  pickProjectVersionFields,
  projectVersionToString,
} from '../utils/projectVersions';

/** Top-level version fields on BPS project API requests (semver strings). */
export interface BpsProjectVersionWireFields {
  version?: string;
  framework_version?: string;
}

/** Parsed top-level version fields from BPS project API responses. */
export interface BpsProjectVersionFields {
  version?: ProjectVersion;
  framework_version?: ProjectVersion;
}

export function parseBpsProjectVersionFields(value: unknown): BpsProjectVersionFields {
  const candidate = pickProjectVersionFields(value);
  return {
    ...(candidate.version && { version: cloneProjectVersion(candidate.version) }),
    ...(candidate.frameworkVersion && { framework_version: cloneProjectVersion(candidate.frameworkVersion) }),
  };
}

export function serializeBpsProjectVersionWireFields(
  value: unknown,
  options: { withDefaults?: boolean } = { withDefaults: true }
): BpsProjectVersionWireFields {
  const candidate = pickProjectVersionFields(value);
  const version = candidate.version ?? (options.withDefaults ? CURRENT_PROJECT_VERSION : undefined);
  const frameworkVersion = candidate.frameworkVersion ?? (options.withDefaults ? CURRENT_FRAMEWORK_VERSION : undefined);

  return {
    ...(version && { version: projectVersionToString(version) }),
    ...(frameworkVersion && { framework_version: projectVersionToString(frameworkVersion) }),
  };
}

export interface CreateProjectRequest {
  pin: string;
  project_string: string;
  title?: string;
  version?: string;
  framework_version?: string;
}

export interface CreateProjectResponse extends BpsProjectVersionFields {
  id: number;
  title?: string;
  share_code: string;
  project_string: string;
  updated_at: string;
  project_type: string;
  is_template: boolean;
}

export interface GetProjectResponse extends BpsProjectVersionFields {
  id: number;
  title?: string;
  share_code: string;
  project_string: string;
  updated_at: string;
  project_type: string;
  is_template: boolean;
}

export type UpdateProjectResponse = BpsProjectVersionFields;

export function isBpsTemplateProject(project: Pick<GetProjectResponse, 'project_type'>): boolean {
  return project.project_type === 'template';
}

export interface UpdateProjectRequest {
  pin: string;
  project_string?: string;
  title?: string;
  version?: string;
  framework_version?: string;
}

export interface DeleteProjectRequest {
  pin: string;
}

export interface ApiError {
  error: string;
}
